/**
 * Мутация, начатая в одной сессии вкладки, не доставляет результат в другую.
 *
 * `onSuccess`/`onError`/`onSettled` пишут в кэш запросов: откат оптимистичной правки кладёт
 * снимок, снятый в `onMutate`, успех — данные из ответа. Ключи кэша от аккаунта не зависят
 * (`['project', id]`, `['projects']`), а `queryClient.clear()` при выходе и входе висящую
 * мутацию не останавливает: её колбэки срабатывали позже, уже в кэше следующего аккаунта, и
 * показывали ему приватные данные прошлого (название карты, слои, список карт).
 *
 * Поколение сессии (`sessionEpoch`) запоминаем в `onMutate`, до запроса, и сверяем на каждом
 * колбэке: `onSuccess` может быть асинхронным, и пока он ждёт, вкладка успевает сменить аккаунт.
 * Исключение — мутация, начатая без входа: вход из её `onSuccess` (подтверждение почты) не глушит
 * её же `onSettled`, а данных прошлой сессии у неё нет, потому что сессии не было.
 */
import type { MutationFunctionContext, MutationOptions } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';

interface MutationRun {
  epoch: number;
  /** Начата без входа: сессия могла появиться только позже. */
  anonymous: boolean;
  /** Решение, принятое на первом колбэке после ответа. */
  current?: boolean;
}

/** Запуск мутации — по контексту, который TanStack создаёт на каждый `execute` и отдаёт всем колбэкам. */
const runs = new WeakMap<MutationFunctionContext, MutationRun>();
/** Уже обёрнутые опции: TanStack прогоняет их через `defaultMutationOptions` повторно. */
const fenced = new WeakSet<object>();

function isCurrentSession(context: MutationFunctionContext | undefined): boolean {
  const run = context ? runs.get(context) : undefined;
  if (!run) {
    // Мутация восстановлена без `onMutate` (гидрация, пауза оффлайн) — поколения не знаем.
    return true;
  }
  const unchanged = run.epoch === useAuthStore.getState().sessionEpoch;
  run.current = (run.current ?? unchanged) && (run.anonymous || unchanged);
  return run.current;
}

/**
 * Для колбэков вызова `mutate(vars, { onError })`: они идут мимо `defaultMutationOptions`, и ограда
 * выше их не видит. Снимок поколения — до правки кэша; проверка — перед записью в кэш.
 */
export function sessionGuard(): () => boolean {
  const { sessionEpoch } = useAuthStore.getState();
  return () => sessionEpoch === useAuthStore.getState().sessionEpoch;
}

/** Опции любой мутации: данные, переменные и контекст у каждой свои. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- паритет с QueryClient#defaultMutationOptions
export type AnyMutationOptions = MutationOptions<any, any, any, any>;

/** Опции мутации, чьи колбэки после ответа молчат, если сессия вкладки сменилась. */
export function fenceSessionMutation<T extends AnyMutationOptions>(options: T): T {
  if (fenced.has(options)) {
    return options;
  }
  const { onMutate, onSuccess, onError, onSettled } = options;
  const wrapped: T = {
    ...options,
    onMutate: (variables, context) => {
      const { sessionEpoch, user } = useAuthStore.getState();
      runs.set(context, { epoch: sessionEpoch, anonymous: user === null });
      return onMutate?.(variables, context);
    },
    onSuccess: (data, variables, result, context) =>
      isCurrentSession(context) ? onSuccess?.(data, variables, result, context) : undefined,
    onError: (error, variables, result, context) =>
      isCurrentSession(context) ? onError?.(error, variables, result, context) : undefined,
    onSettled: (data, error, variables, result, context) =>
      isCurrentSession(context) ? onSettled?.(data, error, variables, result, context) : undefined,
  };
  fenced.add(wrapped);
  return wrapped;
}
